import { useMemo, useState } from "react";
import type { Audience, Locale } from "../api/types";
import { AudiencePattern, DotField } from "../patterns/AudiencePattern";
import { LanguagePicker } from "../components/LanguagePicker";
import { localUi } from "../i18n/uiFallback";

interface Props {
  audiences: Audience[];
  locales: Locale[];
  locale: string;
  ui: Record<string, string>;
  dark: boolean;
  onPick: (audienceSlug: string) => void;
  onLocale: (code: string) => void;
  onDarkToggle: () => void;
}

export function QuizScreen({
  audiences,
  locale,
  locales,
  ui,
  dark,
  onPick,
  onLocale,
  onDarkToggle,
}: Props) {
  const ordered = useMemo(() => {
    const featured = audiences.filter((a) => a.is_featured);
    const rest = audiences.filter((a) => !a.is_featured);
    return [...featured, ...rest];
  }, [audiences]);

  const [, setHover] = useState<string | null>(null);
  const t = (k: string) => ui[k] ?? k;

  return (
    <div className="quiz">
      <div className="quiz__bg" aria-hidden="true">
        <DotField />
      </div>
      <div className="quiz__inner">
        <header className="quiz__head">
          <div className="brand">
            <span className="brand__mark">▼</span>
            <span className="brand__name">{t("brand")}</span>
          </div>
          <div className="quiz__meta">
            <span className="meta-pill mono">{locale.toUpperCase()}</span>
            <span className="meta-pill mono">
              {new Date().toLocaleDateString(locale, {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span className="meta-pill mono">VOL.01</span>
          </div>
        </header>

        <div className="quiz__hero">
          <div className="quiz__eyebrow mono">— {t("tagline")}</div>
          <h1 className="quiz__title">
            {t("quiz_title")
              .split("")
              .map((c, i) => (
                <span
                  key={i}
                  style={{ animationDelay: `${i * 0.04}s` }}
                  className="char"
                >
                  {c === " " ? " " : c}
                </span>
              ))}
          </h1>
          <p className="quiz__sub">{t("quiz_sub")}</p>
        </div>

        <div className="quiz__grid">
          {ordered.map((a, idx) => (
            <button
              key={a.slug}
              className={`acard ${a.is_featured ? "acard--featured" : ""}`}
              onMouseEnter={() => setHover(a.slug)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onPick(a.slug)}
              style={{ animationDelay: `${idx * 0.05}s` }}
              aria-label={a.label}
            >
              <div className="acard__pattern" aria-hidden="true">
                <AudiencePattern id={a.pattern} />
              </div>
              <div className="acard__top">
                <span className="acard__num mono">{String(idx + 1).padStart(2, "0")}</span>
                {a.age_hint && !a.is_featured && (
                  <span className="acard__age mono">{a.age_hint}</span>
                )}
              </div>
              <div className="acard__body">
                <div className="acard__icon">{a.icon}</div>
                <h3 className="acard__name">{a.label}</h3>
                {a.subtitle && <p className="acard__sub">{a.subtitle}</p>}
              </div>
              <div className="acard__cta mono">
                <span>→ {t("keep_going")}</span>
              </div>
              {a.is_featured && (
                <div className="acard__badge mono">★ {localUi(locale, "future")}</div>
              )}
            </button>
          ))}
        </div>

        <footer className="quiz__foot">
          <div className="quiz__foot-left mono">
            {t("page_of")} · 001 / {String(audiences.length).padStart(3, "0")}
          </div>
          <div className="quiz__foot-right">
            <LanguagePicker
              locales={locales}
              value={locale}
              onChange={onLocale}
              label={t("lang")}
            />
            <button className="ghost mono" onClick={onDarkToggle}>
              {dark ? t("light") : t("dark")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
